import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  Timestamp,
  writeBatch
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  LuStore,
  LuImage as LuImageIcon,
  LuCheck,
  LuLoaderCircle,
  LuArrowRight,
  LuMapPin,
  LuClock,
  LuPalette,
  LuInstagram,
  LuCalendar,
  LuUsers,
  LuLayoutList
} from "react-icons/lu";

/* --- CORREÇÃO DA ANIMAÇÃO --- */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled(LuLoaderCircle)`
  animation: ${spin} 1s linear infinite;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3b82f6;
  background-image: radial-gradient(#76a9fc 1px, transparent 1px);
  background-size: 20px 20px;
  padding: 1rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  width: 100%;
  max-width: 650px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;

  @media (min-width: 640px) { padding: 3rem; }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 99px;
  margin-bottom: 2rem;
  overflow: hidden;
  
  div {
    height: 100%;
    background: linear-gradient(90deg, #3b82f6, #60a5fa);
    transition: width 0.4s ease;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  text-align: center;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  svg { color: #2563eb; }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #6b7280;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: 1px solid ${props => props.$error ? '#ef4444' : '#e5e7eb'};
  background: #fff;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    border-color: ${props => props.$error ? '#ef4444' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.$error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.1s, background 0.2s;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);

  &:hover { background: #1d4ed8; }
  &:disabled { background: #93c5fd; cursor: not-allowed; }
  &:active { transform: scale(0.98); }
`;

const LogoUpload = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  margin: 0 auto 1.5rem;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s;
  
  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const ErrorMsg = styled.span`
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
`;

const SuccessMsg = styled.span`
  color: #16a34a;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HorarioRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  
  input { opacity: 0; width: 0; height: 0; }
  
  span {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: #e5e7eb;
    transition: .3s;
    border-radius: 34px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .3s;
      border-radius: 50%;
    }
  }
  
  input:checked + span { background-color: #2563eb; }
  input:checked + span:before { transform: translateX(20px); }
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 2rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ModelCard = styled.div`
  border: 2px solid ${props => props.$selected ? '#2563eb' : '#e5e7eb'};
  background: ${props => props.$selected ? '#eff6ff' : '#fff'};
  color: ${props => props.$selected ? '#2563eb' : '#6b7280'};
  border-radius: 12px;
  padding: 1rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.$selected ? '#2563eb' : '#9ca3af'};
  }

  svg {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 0.9rem;
    font-weight: 600;
  }
`;

function digitsOnly(s = "") { return s.replace(/\D/g, ""); }

function formatPhoneBR(value = "") {
  const v = digitsOnly(value).slice(0, 11);
  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
}

export default function CadastroBarbearia() {
  const db = getFirestore(app);
  const auth = getAuth(app);
  const storage = getStorage(app);
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [isSaving, setIsSaving] = useState(false);
  
  // NOVO ESTADO: Guarda o primeiro nome para usar no proprietário
  const [primeiroNomeOwner, setPrimeiroNomeOwner] = useState("Proprietário");

  const [form, setForm] = useState({
    nome: "",
    subdominio: "",
    telefone: "",
    instagram: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    cidade: "",
    estado: "",
    corPrimaria: "#3a79ed",
    tema: "moderno",
    modeloTrabalho: "agenda", 
    horarios: {
      segunda: { aberto: true, inicio: "09:00", fim: "20:00" },
      terca: { aberto: true, inicio: "09:00", fim: "20:00" },
      quarta: { aberto: true, inicio: "09:00", fim: "20:00" },
      quinta: { aberto: true, inicio: "09:00", fim: "20:00" },
      sexta: { aberto: true, inicio: "09:00", fim: "20:00" },
      sabado: { aberto: true, inicio: "09:00", fim: "18:00" },
      domingo: { aberto: false, inicio: "", fim: "" },
    }
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [subdominioErro, setSubdominioErro] = useState("");
  const [checkingDomain, setCheckingDomain] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) navigate("/login");
      else {
        const leadSnap = await getDoc(doc(db, "leads", user.uid));
        if (leadSnap.exists()) {
          const data = leadSnap.data();

          // LÓGICA DO NOME: Pega o primeiro nome, capitaliza e salva no estado
          const rawName = data.nome || "";
          const pNome = rawName.split(" ")[0] || "Proprietário";
          const nomeFormatado = pNome.charAt(0).toUpperCase() + pNome.slice(1).toLowerCase();
          setPrimeiroNomeOwner(nomeFormatado);

          setForm(prev => ({
            ...prev,
            nome: prev.nome || data.nome + " Barber",
            telefone: prev.telefone || data.telefone
          }));
        }
      }
    });
    return () => unsubscribe();
  }, [auth, db, navigate]);

  useEffect(() => {
    const domain = form.subdominio.trim().toLowerCase();
    if (domain.length < 3) return;
    
    const timeout = setTimeout(async () => {
      setCheckingDomain(true);
      const isValid = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(domain);
      
      if (!isValid) {
        setSubdominioErro("Apenas letras minúsculas, números e hífens. (Não pode ter espaços ou caracteres especiais)");
        setCheckingDomain(false);
        return;
      }
      
      const docSnap = await getDoc(doc(db, "barbearia", domain));
      if (docSnap.exists()) setSubdominioErro("Indisponível");
      else setSubdominioErro("");
      
      setCheckingDomain(false);
    }, 600);
    return () => clearTimeout(timeout);
  }, [form.subdominio, db]);

  const handleNext = () => {
    if (step === 1 && (subdominioErro || !form.subdominio)) return;
    setStep(prev => prev + 1);
  };
  
  const handleImage = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogoPreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const buscarCep = async (cep) => {
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, "")}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(prev => ({ ...prev, endereco: data.logradouro, cidade: data.localidade, estado: data.uf }));
      }
    } catch (e) {}
  };

  const handleFinalSubmit = async () => {
    setIsSaving(true);
    const user = auth.currentUser;
    const sub = form.subdominio.toLowerCase();
    
    try {
      let logoUrl = "";
      if (logoFile) {
        const logoRef = ref(storage, `${sub}/logo.jpg`);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      }

      const agendaAberta = form.modeloTrabalho === 'agenda' || form.modeloTrabalho === 'ambos';
      const filaAberta = form.modeloTrabalho === 'fila' || form.modeloTrabalho === 'ambos';

      const batch = writeBatch(db);
      
      const barbeariaRef = doc(db, "barbearia", sub);
      batch.set(barbeariaRef, {
        nome: form.nome,
        subdominio: sub,
        telefone: form.telefone,
        telefoneRaw: digitsOnly(form.telefone),
        instagram: form.instagram,
        logo: logoUrl,
        corPrimaria: form.corPrimaria,
        tema: form.tema,
        agendaAberta: agendaAberta,
        filaAberta: filaAberta,
        criadorUid: user.uid,
        criadoEm: Timestamp.now(),
        endereco: {
          cep: form.cep,
          endereco: form.endereco,
          numero: form.numero,
          complemento: form.complemento,
          cidade: form.cidade,
          estado: form.estado
        },
        SituacaoCadastral: {
          statusServico: "pendente",
          statusBarbeiro: "pendente"
        },
        plan: { 
            status: 'trialing', 
            trialEnd: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) 
        }
      });

      const horariosRef = collection(db, `barbearia/${sub}/horarioFuncionamento`);
      Object.entries(form.horarios).forEach(([dia, cfg]) => {
         batch.set(doc(horariosRef, dia), {
           aberto: cfg.aberto,
           horarioInicio: cfg.inicio,
           horarioTermino: cfg.fim
         });
      });

      const barbeiroRef = doc(db, `barbearia/${sub}/barbeiros`, user.uid);
      batch.set(barbeiroRef, {
        uid: user.uid,
        nome: primeiroNomeOwner, // <--- AQUI ESTÁ A MUDANÇA (Usa a variável do estado)
        cargo: "proprietario",
        foto: logoUrl,
        servicosIds: []
      });

      await batch.commit();
      
      navigate("/cadastro-servico");
      
    } catch (e) {
      console.error(e);
      alert("Erro ao salvar: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <Card>
        <ProgressBar>
          <div style={{ width: `${(step/totalSteps)*100}%` }} />
        </ProgressBar>

        {step === 1 && (
          <div style={{ animation: 'fadeIn 0.5s' }}>
            <Title><LuStore /> Identidade</Title>
            <Subtitle>Defina o nome e o link da sua barbearia.</Subtitle>
            
            <input type="file" id="logo" hidden accept="image/*" onChange={handleImage} />
            <LogoUpload htmlFor="logo">
              {logoPreview ? <img src={logoPreview} alt="Logo" /> : <LuImageIcon size={32} color="#9ca3af" />}
            </LogoUpload>
            
            <FormGroup>
              <Label>Nome da Barbearia</Label>
              <Input 
                value={form.nome} 
                onChange={e => setForm({...form, nome: e.target.value})} 
                placeholder="Ex: Barbearia do João"
              />
            </FormGroup>

            <FormGroup>
              <Label>Link (<strong>nome</strong>.bigode.app)</Label>
              <Input 
                value={form.subdominio} 
                $error={!!subdominioErro}
                onChange={e => {
                   setForm({...form, subdominio: e.target.value});
                   setSubdominioErro("");
                }} 
                placeholder="minhabarbearia"
              />
              {checkingDomain && <span style={{fontSize: '0.8rem', color:'#6b7280'}}>Verificando...</span>}
              {subdominioErro && <ErrorMsg>{subdominioErro}</ErrorMsg>}
              {!subdominioErro && form.subdominio.length > 3 && !checkingDomain && <SuccessMsg><LuCheck size={14}/> Disponível</SuccessMsg>}
            </FormGroup>
            
            <Button onClick={handleNext} disabled={!form.nome || !form.subdominio || !!subdominioErro}>
              Próximo <LuArrowRight />
            </Button>
          </div>
        )}

        {step === 2 && (
           <div style={{ animation: 'fadeIn 0.5s' }}>
             <Title><LuMapPin /> Localização</Title>
             <Subtitle>Onde seus clientes te encontram?</Subtitle>
             
             <div style={{display:'flex', gap:10}}>
               <div style={{flex:1}}>
                 <Label>CEP</Label>
                 <Input 
                   value={form.cep} 
                   onChange={e => {
                     const v = e.target.value.replace(/\D/g,"");
                     setForm({...form, cep: v});
                     if(v.length === 8) buscarCep(v);
                   }} 
                   placeholder="00000000"
                   maxLength={8}
                 />
               </div>
               <div style={{flex:2}}>
                 <Label>Cidade</Label>
                 <Input value={form.cidade} onChange={e => setForm({...form, cidade: e.target.value})}  placeholder="Ex: São Caetano do Sul"/>
               </div>
             </div>
             
             <FormGroup style={{marginTop: '1rem'}}>
               <Label>Endereço</Label>
               <Input value={form.endereco} onChange={e => setForm({...form, endereco: e.target.value})} placeholder="Ex: Rua João e Maria"/>
             </FormGroup>
             
             <div style={{display:'flex', gap:10}}>
               <div style={{flex:1}}>
                 <Label>Número</Label>
                 <Input value={form.numero} onChange={e => setForm({...form, numero: e.target.value})}  placeholder="Ex: 1234"/>
               </div>
               <div style={{flex:1}}>
                 <Label>WhatsApp</Label>
                 <Input 
                   value={form.telefone} 
                   onChange={e => setForm({...form, telefone: formatPhoneBR(e.target.value)})} 
                   placeholder="(00) 00000-0000"
                 />
               </div>
             </div>

             <div style={{marginTop: '2rem', display:'flex', gap: 10}}>
               <Button style={{background: '#f3f4f6', color: '#374151'}} onClick={() => setStep(1)}>Voltar</Button>
               <Button onClick={handleNext}>Próximo</Button>
             </div>
           </div>
        )}

        {step === 3 && (
           <div style={{ animation: 'fadeIn 0.5s' }}>
             <Title><LuClock /> Funcionamento</Title>
             <Subtitle>Como e quando você atende?</Subtitle>

             <Label style={{marginBottom: 8}}>Modelo de Trabalho</Label>
             <ModelGrid>
                <ModelCard 
                  $selected={form.modeloTrabalho === 'agenda'}
                  onClick={() => setForm({...form, modeloTrabalho: 'agenda'})}
                >
                  <LuCalendar />
                  <span>Agenda</span>
                </ModelCard>

                <ModelCard 
                  $selected={form.modeloTrabalho === 'fila'}
                  onClick={() => setForm({...form, modeloTrabalho: 'fila'})}
                >
                  <LuUsers />
                  <span>Fila</span>
                </ModelCard>

                <ModelCard 
                  $selected={form.modeloTrabalho === 'ambos'}
                  onClick={() => setForm({...form, modeloTrabalho: 'ambos'})}
                >
                  <LuLayoutList />
                  <span>Ambos</span>
                </ModelCard>
             </ModelGrid>

             <Label>Horários de Atendimento</Label>
             <div style={{maxHeight: 250, overflowY: 'auto', paddingRight: 5}}>
               {Object.keys(form.horarios).map(dia => (
                 <HorarioRow key={dia}>
                   <div style={{display:'flex', alignItems:'center', gap: 10}}>
                     <ToggleSwitch>
                       <input 
                         type="checkbox" 
                         checked={form.horarios[dia].aberto}
                         onChange={e => setForm({
                           ...form, 
                           horarios: {
                             ...form.horarios, 
                             [dia]: {...form.horarios[dia], aberto: e.target.checked}
                           }
                         })}
                       />
                       <span />
                     </ToggleSwitch>
                     <strong style={{textTransform: 'capitalize', fontSize: '0.9rem'}}>{dia}</strong>
                   </div>
                   
                   {form.horarios[dia].aberto ? (
                     <div style={{display:'flex', gap: 5}}>
                       <input 
                         type="time" 
                         value={form.horarios[dia].inicio}
                         onChange={e => setForm({
                           ...form, 
                           horarios: {
                             ...form.horarios, 
                             [dia]: {...form.horarios[dia], inicio: e.target.value}
                           }
                         })}
                         style={{border:'1px solid #e5e7eb', borderRadius: 8, padding: 4}}
                       />
                       <input 
                         type="time" 
                         value={form.horarios[dia].fim}
                         onChange={e => setForm({
                           ...form, 
                           horarios: {
                             ...form.horarios, 
                             [dia]: {...form.horarios[dia], fim: e.target.value}
                           }
                         })}
                         style={{border:'1px solid #e5e7eb', borderRadius: 8, padding: 4}}
                       />
                     </div>
                   ) : <span style={{fontSize:'0.8rem', color:'#9ca3af'}}>Fechado</span>}
                 </HorarioRow>
               ))}
             </div>

             <div style={{marginTop: '2rem', display:'flex', gap: 10}}>
               <Button style={{background: '#f3f4f6', color: '#374151'}} onClick={() => setStep(2)}>Voltar</Button>
               <Button onClick={handleNext}>Próximo</Button>
             </div>
           </div>
        )}

        {step === 4 && (
           <div style={{ animation: 'fadeIn 0.5s' }}>
             <Title><LuPalette /> Personalizar</Title>
             <Subtitle>Deixe com a sua cara.</Subtitle>

             <FormGroup>
               <Label>Cor Principal</Label>
               <div style={{display:'flex', alignItems:'center', gap: 10, padding: 10, border:'1px solid #e5e7eb', borderRadius: 12}}>
                 <input 
                   type="color" 
                   value={form.corPrimaria} 
                   onChange={e => setForm({...form, corPrimaria: e.target.value})}
                   style={{minWidth:40, height:40, border:'none', background:'none', cursor:'pointer'}} 
                 />
                 <span style={{fontSize:'0.9rem', color:'#6b7280'}}>Cor dos detalhes do seu site que você poderá compartilhar com seus clientes</span>
               </div>
             </FormGroup>

             <FormGroup>
               <Label>Instagram (sem @)</Label>
               <div style={{position:'relative'}}>
                 <LuInstagram style={{position:'absolute', left: 12, top: 14, color:'#9ca3af'}} />
                 <Input 
                   style={{paddingLeft: 36}} 
                   value={form.instagram}
                   onChange={e => setForm({...form, instagram: e.target.value})}
                   placeholder="barbearia"
                 />
               </div>
             </FormGroup>

             <div style={{marginTop: '2rem', display:'flex', gap: 10}}>
               <Button style={{background: '#f3f4f6', color: '#374151'}} onClick={() => setStep(3)}>Voltar</Button>
               <Button onClick={handleFinalSubmit} disabled={isSaving}>
                 {isSaving ? <><Spinner /> Salvando...</> : "Finalizar Cadastro"}
               </Button>
             </div>
           </div>
        )}
      </Card>
    </Container>
  );
}